<template>
    <ReloadPrompt />

    <q-layout view="hHh Lpr fFf">
        <AppBar />

        <SideBar
            @sync-data="syncData()"
            @log-in="logIn()"
            @log-out="logOut()"
        />

        <q-page-container>
            <router-view
                @sync-data="syncData()"
                @log-in-and-update="logInAndUpdate()"
            />
        </q-page-container>

        <q-page-sticky position="bottom-right" :offset="[20, 20]" class="right-sided">
            <q-card flat bordered class="bg-grey-2 support-card q-mb-md" v-if="supportOpen">
                <q-card-section v-html="ts('supportText')" />
            </q-card>
            <q-btn
                fab
                icon="contact_support"
                color="secondary"
                @click="supportOpen = !supportOpen"
                :title="ts('programSupport')"
            />
        </q-page-sticky>

        <q-ajax-bar position="bottom" color="accent" size="10px" />
    </q-layout>
</template>

<script setup lang="ts">
// Purpose: Describe the whole application and construct main app layout

import Confirmation from '@/components/dialogs/Confirmation.vue'
import InfoBox from '@/components/dialogs/InfoBox.vue'
import LogIn from '@/components/dialogs/LogIn.vue'
import ReloadPrompt from '@/components/ReloadPrompt.vue'
import SideBar from '@/components/SideBar.vue'
import { apiLogOut, authenticate, showError } from '@/network/auth'
import { updater } from '@/network/updater'
import { Defaults, Options } from '@/orm/models/Setting'
import SettingsRepo from '@/orm/repositories/SettingsRepo'
import { ts } from '@/plugins/i18nFormat'
import { useRepo } from 'pinia-orm'
import { useQuasar } from 'quasar'
import { computed, ref } from 'vue'
import AppBar from './components/AppBar.vue'

const quasar = useQuasar()

useRepo(SettingsRepo).triggerActions(Options.Language)
useRepo(SettingsRepo).triggerActions(Options.DbStructureVersion)

const isAuthenticated = computed(() => (useRepo(SettingsRepo).getOption(Options.AuthToken, Defaults.AuthToken) !== ''))

const supportOpen = ref(false)
const syncInProgress = ref(false)

function logInAndUpdate(event) {
    logIn(event, 3, 0, true)
}

async function syncData(event, force = false, firstTime = true) {
    if (syncInProgress.value && !force)
        return

    if (!isAuthenticated.value && !force) {
        syncInProgress.value = true

        quasar.dialog({
            component: Confirmation,
            componentProps: {
                icon: 'lock',
                question: ts('loginBeforeDataUpdate'),
                yes: ts('login'),
                no: ts('justUpdate'),
            },
        }).onOk(async () => {
            syncInProgress.value = false
            await logIn(event, 3, 0, true, firstTime)
        }).onCancel(async () => {
            await updater()
            syncInProgress.value = false
        })
    } else {
        // updater() can return false in case detect logout -> than try again through log in dialog
        if (!await updater() && firstTime) {
            // Do nothing here for now to prevent loops, user will get notification about expired session
        }
    }
}

function logIn(event, attempts = 3, error = 0, updateDataAtTheEnd = false, firstTime = true) {
    quasar.dialog({
        component: LogIn,
        componentProps: {
            errorMsg: showError(error, firstTime),
        },
    }).onOk(async (payload) => {
        const lastError = await authenticate(payload.username, payload.password)

        if (!isAuthenticated.value) {
            if (attempts > 1)
                logIn(event, attempts - 1, lastError, updateDataAtTheEnd, firstTime)
            else
                quasar.dialog({
                    component: InfoBox,
                    componentProps: {
                        message: ts('tooManyAttempts'),
                    },
                })
        } else {
            if (updateDataAtTheEnd)
                updater()
            else {
                quasar.dialog({
                    component: Confirmation,
                    componentProps: {
                        icon: 'update',
                        question: ts('updateDataNow'),
                    },
                }).onOk(() => {
                    updater()
                })
            }
        }
    })
}

function logOut() {
    quasar.dialog({
        component: Confirmation,
        componentProps: {
            icon: 'logout',
            question: ts('reallyLogOut'),
        },
    }).onOk(() => (apiLogOut()))
}
</script>

<style lang="sass">
body, .q-field, .q-btn, .q-table tbody td, .q-table thead th
    font-size: 1em !important

.support-card
    width: 360px
    text-align: left

.support-card p:last-child
    margin-bottom: 0

.right-sided
    text-align: right
</style>
